import { BarSeriesOption } from "echarts";
import { useEffect, useMemo } from "react";

import { LegendItem, useLegends } from "@/hooks/shared/useLegends";
import Palette from "@/Utils/Colors/palette";

import { RelianceRankingResponse } from "../api/useRelianceRankingV2";
import { useBusinessTermMapping } from "../utils/businessTermMapping";

type UseBusinessUnitChartOptionsProps = {
  relianceRankingData: RelianceRankingResponse | undefined;
  palette: Palette;
};

export const useBusinessUnitChartOptions = ({
  relianceRankingData,
  palette,
}: UseBusinessUnitChartOptionsProps): {
  axisLabels: string[];
  seriesData: BarSeriesOption[];
  legends: LegendItem[];
  handleLegendItemClick: (legendId: string) => void;
  totals: number[];
} => {
  const { legends, setLegends, handleLegendItemClick } = useLegends([]);
  const { mapBusinessTermName } = useBusinessTermMapping();

  useEffect(() => {
    if (relianceRankingData?.saleCategories && relianceRankingData.saleCategories.length > 0) {
      // Sort categories by their total contribution (descending order)
      const sortedCategories = [...relianceRankingData.saleCategories]
        .filter((category) => relianceRankingData.totals[category] !== 0)
        .sort((a, b) => {
          return (relianceRankingData.totals[b] ?? 0) - (relianceRankingData.totals[a] ?? 0);
        });

      const newLegends = sortedCategories.map((channel, index) => ({
        id: channel,
        title: mapBusinessTermName(channel),
        color: palette.getSequentialColor(index).color,
        isActive: true,
      }));
      setLegends(newLegends);
    }
  }, [relianceRankingData?.saleCategories, relianceRankingData?.totals]);

  const seriesData = useMemo(() => {
    if (!relianceRankingData?.chartView) return [];

    return legends
      .filter((legend) => legend.isActive)
      .map((legend) => {
        return {
          name: legend.title,
          type: "bar" as const,
          color: legend.color,
          data: relianceRankingData.chartView.map((item) => item.contribution?.[legend.id] ?? 0),
        };
      });
  }, [relianceRankingData?.chartView, legends]);

  const axisLabels = relianceRankingData?.chartView.map((item) => item.business_unit) ?? [];
  const totals = (relianceRankingData?.chartView ?? []).map((item) =>
    Number(item.contribution?.total ?? 0)
  );

  return {
    axisLabels,
    seriesData,
    legends,
    handleLegendItemClick,
    totals,
  };
};
